/**
 * «Base de datos» (`/activa/oficial/base`): título, los números de la base y las sub-pestañas
 * Contactos · Públicos (SPEC-BD §6.1). Desde el 17/10/2026 no está en el menú: las cargas están en Público (pestaña
 * «Con consentimiento») y se llega acá desde «Un público de tu base» del asistente.
 *
 * Los números se piden de nuevo al cambiar de pantalla: después de importar o borrar, volver a la lista
 * tiene que mostrar la base como quedó. Si el resumen falla no se muestra la fila (nunca unos ceros que
 * parezcan «no hay nada»), y el resto funciona igual.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { Users, UsersRound } from "lucide-react";
import type { ResumenBase } from "../../types.base";
import { baseApi } from "../../baseApi";
import { num } from "../../../campana-nueva/utils/format";
import { R_BASE, llamar } from "../../components/base/comun";

const TABS: { label: string; path: string; icon: ReactNode }[] = [
  { label: "Contactos", path: `${R_BASE}/contactos`, icon: <Users size={14} /> },
  { label: "Públicos", path: `${R_BASE}/publicos`, icon: <UsersRound size={14} /> },
];

export interface ContextoBase {
  recargarResumen: () => void;
}

const TERMINADA = new Set(["importada", "fallida", "eliminada"]);

/**
 * Una importación (o un borrado) termina por polling, sin cambiar de URL: los números de arriba seguían
 * en 0 con los 10 contactos ya importados. Cuando la carga llega a un estado final, se piden de nuevo.
 * Al abrir una carga que ya estaba terminada no se piden: el layout acaba de hacerlo.
 */
export function useResumenAlTerminar(estado: string | undefined) {
  const ctx = useOutletContext<ContextoBase | undefined>();
  const antes = useRef(estado);
  useEffect(() => {
    if (antes.current !== undefined && antes.current !== estado && estado && TERMINADA.has(estado)) ctx?.recargarResumen();
    antes.current = estado;
  }, [estado, ctx]);
}

export default function BaseLayout() {
  const { pathname } = useLocation();
  const [resumen, setResumen] = useState<ResumenBase | null>(null);
  const [pedido, setPedido] = useState(0);
  const seq = useRef(0);
  const contexto = useMemo<ContextoBase>(() => ({ recargarResumen: () => setPedido((n) => n + 1) }), []);

  useEffect(() => {
    const mio = ++seq.current;
    void (async () => {
      const l = await llamar<ResumenBase>(() => baseApi.resumen());
      // Una respuesta vieja (se cambió de pantalla dos veces seguidas) no pisa la nueva.
      if (mio !== seq.current) return;
      setResumen(l.ok && l.data ? l.data : null);
    })();
  }, [pathname, pedido]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Base de datos</h2>
        <p className="text-sm text-slate-500 mt-0.5">Tus contactos cargados desde Excel, con su consentimiento, y los públicos que armás con ellos</p>
      </div>

      {resumen && (
        <dl className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <Numero label="Contactos" valor={resumen.contactos} />
          <Numero label="Con consentimiento" valor={resumen.conConsentimiento} tono="text-emerald-700" />
          <Numero label="En no contactar" valor={resumen.enNoContactar} tono={resumen.enNoContactar ? "text-red-700" : undefined} />
          <Numero label="Públicos" valor={resumen.publicos} />
        </dl>
      )}

      <nav aria-label="Secciones de la base de datos" className="flex items-center gap-1 border-b border-slate-200 -mx-1 overflow-x-auto whitespace-nowrap">
        {TABS.map((t) => (
          <NavLink key={t.path} to={t.path} end={false}
            className={({ isActive }) => `relative flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800"
            }`}>
            {({ isActive }) => (
              <>
                {t.icon}
                {t.label}
                {isActive && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-slate-900 rounded-t-full" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <Outlet context={contexto} />
    </div>
  );
}

function Numero({ label, valor, tono }: { label: string; valor: number; tono?: string }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-2xl font-semibold tracking-tight mt-0.5 ${tono ?? "text-slate-900"}`}>{num(valor)}</dd>
    </div>
  );
}
